import * as THREE from "three";
// import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Character, MapNode, SubInstance } from "../../../types";
import { getNodeModelCoords, renderNodeItems, renderSubMapCharactersBySubId } from "../helpers/helpers";
import { expGameInstance, mdlHeights } from "../ThreeTestFC";
import { Monster, Npc } from "../../../types/LogicTypes";
import { animateHorizontal } from "../helpers/uiHelpers";
import { getNode, getNodeByChar } from "../helpers/dataHelpers";

export const camera = (): THREE.PerspectiveCamera => {
    const camera = new THREE.PerspectiveCamera(70, window.innerWidth / Math.max(document.documentElement.clientHeight, window.innerHeight), 1, 500);
    camera.position.x = 0;
    camera.position.y = 75;
    camera.position.z = 0;
    camera.lookAt(0, 0, 0);
    return camera
}

export const pointLight = (): THREE.PointLight => {
    const l = new THREE.PointLight(new THREE.Color(0xffffff), 8000)
    return l
}

export const getSquare = (color: number, opacity: number, x: number, y: number): THREE.Mesh => {
    const geometry = new THREE.PlaneGeometry(x, y);
    const material = new THREE.MeshStandardMaterial({ color: color, opacity: opacity, transparent: true });
    const mesh = new THREE.Mesh(geometry, material)
    mesh.rotateX(-Math.PI/2)
    return mesh
}

export const getImageSquare = (img: string, x: number, y: number): THREE.Mesh => {
    const geometry = new THREE.PlaneGeometry(x, y);
    const texture = new THREE.TextureLoader().load(img);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
    const mesh = new THREE.Mesh(geometry, material)
    mesh.rotateX(-Math.PI/2)
    return mesh
}

export const ground = async (map: string): Promise<THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>> => {
    const texture = new THREE.TextureLoader().load(map);
    texture.colorSpace = THREE.SRGBColorSpace;
    const g = new THREE.PlaneGeometry(100, 100)
    g.rotateX(-Math.PI/2)
    const material = new THREE.MeshStandardMaterial({ map: texture });
    const mesh = new THREE.Mesh(g, material);
    mesh.name = "baseMap-mesh"
    mesh.userData.name = "Ground"
    mesh.userData.type = "ground"
    return mesh
}

export const pos = (x: number, y: number, z: number, model: any) => {
    model.position.x = x
    model.position.y = y
    model.position.z = z
    return model
}

// export const applyControls = (cam: THREE.PerspectiveCamera, e: HTMLCanvasElement) => {
//     const controls = new OrbitControls(cam, e)
//     // controls.enabled = false
//     controls.enableDamping = false
//     controls.dampingFactor = .25
//     controls.enableZoom = true
//     // controls.enableRotate = false
//     controls.enablePan = false
// }

export const character = (c: Character, size: number): THREE.Group => {
    const group = new THREE.Group()
    const texture = new THREE.TextureLoader().load(c.texture.img);
    texture.colorSpace = THREE.SRGBColorSpace;
    const height = size;
    const plane = new THREE.PlaneGeometry(height / (c.texture.h / c.texture.w), height)
    plane.rotateX(-Math.PI/2)
    const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
    const characterMesh = new THREE.Mesh(plane, material);
    characterMesh.userData.type = "character-mesh"
    characterMesh.position.z = -(height/2)

    if (c.stats.health === 0 || c.stats.sanity === 0) {
        characterMesh.rotateY(-Math.PI/2)
        characterMesh.position.z = 0.5
        characterMesh.position.x = 1
    }

    // Animate
    setTimeout(() => {
        if (c.stats.health > 0 && c.stats.sanity > 0) {
            animateHorizontal(group, .1, 1000)
        }
    }, 500);

    // Add move buttons for nodes
    group.add(getMoveBtns(c))

    group.add(characterMesh)
    group.name = `char_${c.id}`
    group.userData.id = `char_${c.id}`
    group.userData.type = "character"
    return group
}

export const getMoveBtns = (c: Character): THREE.Group => {
    const group = new THREE.Group()
    if (c.stats.health === 0 || c.stats.sanity === 0) { return group }
    group.name = "moveBtns"
    if (!expGameInstance) { return group }
    const node = getNodeByChar(expGameInstance, c.id)
    let zPlus = 0;
    for (const conn of (node?.c ?? [])) {
        const aNode = getNode(conn)
        if (!node || !aNode) { continue }
        // const nodeDistance = Math.sqrt((Math.pow(node.x-aNode.x, 2))+(Math.pow(node.y-aNode.y,2)))
        // if (nodeDistance < 4) { continue }
        const btn = getImageSquare("img/assets/ui/move_btn_01.png", 2, 2)
        const btnDistance = 3.5
        const rotAng = Math.atan2(aNode.x - node.x, aNode.y - node.y) + (Math.PI)
        const angle = Math.atan2(node.y - aNode.y, node.x - aNode.x) + (Math.PI)
        btn.userData.type = "moveBtn"
        btn.userData.targetNodeId = aNode.id
        const x = Math.cos(angle) * btnDistance
        const y = Math.sin(angle) * btnDistance
        btn.rotateZ(rotAng)
        btn.position.set(x, 0.2 + zPlus, y)
        zPlus += 0.01
        group.add(btn)
    }
    return group
}

export const npc = (c: Npc, size: number): THREE.Group => {
    const group = new THREE.Group()
    const texture = new THREE.TextureLoader().load(c.texture.img);
    texture.colorSpace = THREE.SRGBColorSpace;
    const height = size;
    const plane = new THREE.PlaneGeometry(height / (c.texture.h / c.texture.w), height)
    plane.rotateX(-Math.PI/2)
    const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
    const mesh = new THREE.Mesh(plane, material);
    mesh.userData.type = "npc-mesh"
    mesh.userData.id = c.id
    mesh.position.z = -(height/2)
    group.add(mesh)
    group.name = `npc_${c.id}`
    group.userData.type = "npc"
    return group
}

export const monster = (c: Monster, size: number): THREE.Group => {
    const group = new THREE.Group()
    const texture = new THREE.TextureLoader().load(c.texture.img);
    texture.colorSpace = THREE.SRGBColorSpace;
    const height = size;
    const plane = new THREE.PlaneGeometry(height / (c.texture.h / c.texture.w), height)
    plane.rotateX(-Math.PI/2)
    const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
    const mesh = new THREE.Mesh(plane, material);
    mesh.userData.type = "monster-mesh"
    mesh.userData.id = c.id
    mesh.position.z = -(height/2)
    group.add(mesh)
    group.name = `monster_${c.id}`
    group.userData.type = "monster"
    return group
}

export const node = (n: MapNode, characterScale: number): THREE.Group => {
    const getDefaultRadius = (type: MapNode["type"]): number => {
        if (type === "city") {
            return 2.5
        }
        if (type === "wilderness") {
            return 2.5
        }
        if (type === "door") {
            return 2
        }
        if (type === "empty") {
            return 1.25
        }
        if (type === "container") {
            return 1.5
        }
        if (["c", "w", "s"].includes(type)) {
            return 1.5
        }
        if (["tp-l", "tp-r"].includes(type)) {
            return 2
        }
        return 1
    }
    const getNodeMaterial = (node: MapNode): THREE.MeshStandardMaterial => {
        if (node.img) {
            const texture = new THREE.TextureLoader().load(node.img);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "s") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_s_01.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "tp-l") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/ship_01_l.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "tp-r") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/ship_01_r.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "w") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_w_01.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "c") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_c_01.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "city") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_city_02.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "wilderness") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_wilderness_02.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "door") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_door_01.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "street") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_base_02.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "container") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_box_01.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        if (node.type === "empty") {
            const texture = new THREE.TextureLoader().load(`img/assets/ui/node_empty_01.png`);
            texture.colorSpace = THREE.SRGBColorSpace;
            const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
            return material
        }
        // Default
        const texture = new THREE.TextureLoader().load(`img/assets/ui/node_base_02.png`);
        texture.colorSpace = THREE.SRGBColorSpace;
        const material = new THREE.MeshStandardMaterial({ map: texture, transparent: true });
        return material
    }

    const group = new THREE.Group()

    const radius = ((characterScale ?? 4) / 2.5) * getDefaultRadius(n.type)

    const geometry = new THREE.PlaneGeometry(radius, radius);
    const material = getNodeMaterial(n)
    const nodeMesh = new THREE.Mesh(geometry, material)
    nodeMesh.rotateX(-Math.PI/2)
    nodeMesh.userData.type = "node"
    nodeMesh.userData.id = n.id
    group.name = `node_${n.id}`
    group.add(nodeMesh)

    // Title
    if (n.title) {
        const titleGeo = new THREE.PlaneGeometry(radius*1.5, radius*0.75);
        const titleTxt = new THREE.TextureLoader().load(n.title);
        titleTxt.colorSpace = THREE.SRGBColorSpace;
        const titleMat = new THREE.MeshStandardMaterial({ map: titleTxt, transparent: true });
        const titleMes = new THREE.Mesh(titleGeo, titleMat)
        titleMes.rotateX(-Math.PI/2)
        titleMes.userData.type = "node"
        titleMes.userData.id = n.id
        titleMes.position.set(titleMes.position.x, 0.01, titleMes.position.z+(radius/2))
        group.add(titleMes)
    }

    // Light
    // const light = new THREE.PointLight(new THREE.Color(0xFFECB3), 20)
    // light.position.set(0, 3, 0)
    // group.add(light)

    return group
}

export const getNodeConnections = (nodes: MapNode[], mapScale?: number): THREE.Group => {
    type Conn = {
        from: string,
        to: string,
        type?: string,
    }
    const connections: Conn[] = []
    for (const node of nodes) {
        for (const conn of node.c) {
            const targetNode = nodes.find(n => n.id === conn)
            let type = undefined
            // console.log("Nodes", node, targetNode)
            if (node.inaccessible || targetNode?.inaccessible) {
                type = "i"
            }
            const existingConnection = connections.find(c => ((c.from === conn && c.to === node.id) || (c.to === conn && c.from === node.id)))
            if (!existingConnection) {
                connections.push({from: conn, to: node.id, type})
            }
        }
    }

    const g = new THREE.Group()

    for (const c of connections) {
        const fromNode = nodes.find(n => n.id === c.from)
        const toNode = nodes.find(n => n.id === c.to)
        const from = {x: fromNode?.x ?? 0, y: fromNode?.y ?? 0}
        const to = {x: toNode?.x ?? 0, y: toNode?.y ?? 0}
        g.add(getConnectionLine(from, to, mapScale, c.type))
    }

    return g
}

const getConnectionLine = (from: {x: number, y: number}, to: {x: number, y: number}, mapScale?: number, type?: string): THREE.Group => {

    const getMaterial = (type?: string) => {
        if (type === "i") {
            return new THREE.MeshStandardMaterial({ color: 0xffffff, opacity: .5, transparent: true });
        }
        return new THREE.MeshStandardMaterial({ color: 0x000000, opacity: .5, transparent: true });
    }

    const g = new THREE.Group()

    const dx = to.x - from.x
    const dy = to.y - from.y

    const lineW = 1 / ((mapScale ?? 100) / 100) * 0.3

    // const lineW = 0.3
    const lineH = (Math.sqrt(dx*dx + dy*dy))
    const angle = Math.atan2(dx, dy)

    const geometry = new THREE.PlaneGeometry(lineW, lineH);
    const material = getMaterial(type)

    const line = new THREE.Mesh(geometry, material)
    line.position.set((from.x + to.x)/2, mdlHeights.paths, (from.y + to.y)/2)

    line.rotateX(-Math.PI / 2)
    line.rotateZ(angle)

    g.add(line)
    return g
}

export const createSubmap = (subInstance: SubInstance): THREE.Group => {
    const g = new THREE.Group()
    g.name = `submap_${subInstance.id}`
    g.userData.type = "submap"
    g.userData.subInstanceId = subInstance.id

    const texture = new THREE.TextureLoader().load(subInstance.map.img);
    texture.colorSpace = THREE.SRGBColorSpace;
    const geometry = new THREE.PlaneGeometry(100, 100)
    geometry.rotateX(-Math.PI/2)
    const material = new THREE.MeshStandardMaterial({ map: texture });
    // const material = new THREE.MeshStandardMaterial({ color: 0x000000, opacity: 0.35, transparent: true });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.type = "subMap-mesh"

    for (const n of subInstance.map.nodes) {
        const nodeGroup = node(n, subInstance.map.characterScale ?? 4)
        g.add(pos(n.x, mdlHeights.nodes, n.y, nodeGroup));
        renderNodeItems(n.id, subInstance.id, nodeGroup)
    }

    g.add(getNodeConnections(subInstance.map.nodes, subInstance.map.mapScale))

    setTimeout(() => { renderSubMapCharactersBySubId(subInstance.id); }, 100)

    g.add(mesh)

    if (subInstance.map.placeOnNode) {
        const pos = getNodeModelCoords(subInstance.map.placeOnNode)
        g.position.set(pos?.x ?? 0, mdlHeights.submaps, pos?.z ?? 0)
    } else {
        g.position.set(0, mdlHeights.submaps, 0)
    }

    // g.visible = subInstance.characters.length > 0
    if (subInstance.characters.length > 0) {
        g.scale.set((subInstance.map.mapScale ?? 100) / 100, 1, (subInstance.map.mapScale ?? 100) / 100)
    } else {
        g.scale.set(0, g.scale.y, 0)
    }
    return g

}